import { useEffect, useState, Suspense } from 'react';
import { useAuth } from 'src/contexts/AuthContext';
import dynamic from 'next/dynamic';
import { RegisteredTeamContainerProps } from './containers/RegisteredTeamContainer';
import { JoinedTeamContainerProps } from './containers/JoinedTeamContainer';
import { RegisteredSoloContainerProps } from './containers/RegisteredSoloContainer';
import { RegisterEntryContainerProps } from './containers/RegisterEntryContainer';
import { Tournament } from 'functions/src/types/firestore/Game/Tournament';
import { Team } from 'functions/src/types/firestore/Game/Tournament/Guestlist';
import { parseRegistrationData, getUserType } from './parseRegistrationData';
import { InviteGuard } from 'src/components/guards/InviteGuard';
import { LottieLoader } from 'src/components/LottieLoader';
import {
  toAvatar,
  toName,
} from 'functions/src/types/firestore/Game/Tournament/Guestlist/util';
import { registrationFunctions } from './registrationFunctions';

export type RegistrationDisplayType =
  | 'unregistered'
  | 'solo'
  | 'captain'
  | 'accepted';

export type CommonRegistrationProps = {
  prizePool: {
    amount: number;
    currency: string;
  };
  bracketType: string;
  tournamentCheckInDate?: Date;
  tournamentStartDate: Date;
  tournamentEndDate: Date;
  tournamentReadyDate: Date;
  tournamentName: string;
  gameTitle: string;
  region: string;
  tournamentSponsors: {
    image: string;
    name: string;
  }[];
  skipCheckIn?: boolean;
};

export type RegistrationProps = Pick<
  Tournament<Date>,
  | 'prize'
  | 'region'
  | 'gameTitle'
  | 'gameId'
  | 'title'
  | 'bracketType'
  | 'checkInTime'
  | 'date'
  | 'endDate'
  | 'id'
  | 'maxTeamCount'
  | 'maxTeamSize'
  | 'sponsors'
  | 'subscribedUserIds'
  | 'phase'
  | 'readyTime'
  | 'registrationOptions'
  | 'tournamentDetails'
  | 'skipCheckIn'
  | 'requirementTokens'
> & {
  checkInOptions: Tournament<Date>['registrationOptions']; // TODO: Remove after March 1st
  teams: Team<Date>[];
};

export type RegistrationStateProps = (
  | RegisteredTeamContainerProps
  | RegisteredSoloContainerProps
  | RegisterEntryContainerProps
  | JoinedTeamContainerProps
) & {
  type: RegistrationDisplayType;
};

const DynamicJoinedTeamContainer = dynamic(async () => {
  const component = await import('./containers/JoinedTeamContainer');
  return component.JoinedTeamContainer;
});

const DynamicCreatedTeamContainer = dynamic(async () => {
  const component = await import('./containers/RegisteredTeamContainer');
  return component.RegisteredTeamContainer;
});

const DynamicRegisterPreTournamentContainer = dynamic(async () => {
  const component = await import('./containers/RegisterEntryContainer');
  return component.RegisterEntryContainer;
});

const DynamicRegisteredSoloContainer = dynamic(async () => {
  const component = await import('./containers/RegisteredSoloContainer');
  return component.RegisteredSoloContainer;
});

export function Registration({
  title,
  phase,
  gameTitle,
  gameId,
  prize,
  checkInTime,
  readyTime,
  date,
  endDate,
  id,
  sponsors,
  tournamentDetails,
  // isLive,
  // maxTeamCount,
  region,
  bracketType,
  maxTeamSize,
  teams,
  subscribedUserIds,
  registrationOptions,
  checkInOptions,
  skipCheckIn,
  requirementTokens,
}: RegistrationProps) {
  const [data, setData] = useState<RegistrationStateProps | null>();
  const [userType, setUserType] = useState<RegistrationDisplayType | null>(
    null,
  );
  const { user } = useAuth();
  const containers = {
    accepted: DynamicJoinedTeamContainer,
    solo: DynamicRegisteredSoloContainer,
    captain: DynamicCreatedTeamContainer,
    unregistered: DynamicRegisterPreTournamentContainer,
    '': () => {
      return <></>;
    },
  };
  useEffect(() => {
    const handler = async () => {
      const uid = user?.uid;
      const relevantTeams = teams.filter((team) => {
        return team.userIds.includes(uid || '');
      });
      const soloTeams = teams.filter((team) => {
        return team.userIds.length === 1;
      });
      const soloUsers = soloTeams.map((team) => {
        return team.userIds[0];
      });
      const foundTeam = relevantTeams.find((team) => {
        return team.members.some((member) => {
          return (
            member.userId === uid &&
            (member.status === 'captain' ||
              member.status === 'accepted' ||
              member.status === 'reassigned')
          );
        });
      });
      const teamsWithInvites = relevantTeams.filter((team) => {
        return team.members.some((member) => {
          return member.userId === uid && member.status === 'pending';
        });
      });

      const parsedData = Object.assign(
        {},
        parseRegistrationData({
          bracketType,
          checkInTime,
          date,
          endDate,
          foundTeam,
          gameId,
          gameTitle,
          id,
          maxTeamSize,
          phase,
          prize,
          region,
          sponsors,
          subscribedUserIds,
          teamsWithInvites,
          title,
          userId: user?.uid,
          readyTime,
          registrationOptions,
          checkInOptions,
          tournamentDetails,
          soloUsers,
          skipCheckIn,
          requirementTokens,
        }),
        registrationFunctions({
          userId: user?.uid,
          foundTeam,
          id,
          gameId,
          title,
        }),
      );
      try {
        const type = await getUserType(user?.uid as string, relevantTeams);
        setUserType(type);
        setData(
          Object.assign({}, parsedData, {
            type: type,
            isCaptain: type === 'captain',
            teamName: foundTeam ? toName(foundTeam, true) : undefined,
            teamAvatar: foundTeam ? toAvatar(foundTeam, true) : undefined,
          } as RegistrationStateProps),
        );
      } catch (error) {
        console.error(error);
      }
    };
    handler();
  }, [
    teams,
    user?.uid,
    id,
    bracketType,
    date,
    endDate,
    gameId,
    gameTitle,
    maxTeamSize,
    phase,
    prize,
    region,
    sponsors,
    subscribedUserIds,
    title,
    checkInTime,
    readyTime,
    registrationOptions,
    tournamentDetails,
    checkInOptions,
    skipCheckIn,
    requirementTokens,
  ]);
  const Stage = containers[`${userType}`];

  const handleCheckIn = async (entireTeam: boolean, userId?: string) => {
    if (!user?.uid) {
      return;
    }
    const { checkIn } = await import(
      '../../../firebaseCloud/tournament/checkIn'
    );

    if (data) {
      await checkIn({
        gameId: data.gameId,
        tournamentId: data.tournamentId,
        userId: userId || user?.uid,
        entireTeam,
      });
    }
  };

  return !data ? (
    <LottieLoader sx={{ width: '40px', height: '40px' }} />
  ) : (
    <Suspense>
      <InviteGuard gameId={data.gameId} />
      <Stage {...data} checkIn={handleCheckIn} />
    </Suspense>
  );
}
